import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Compass, ShieldCheck, Sparkles, Network } from "lucide-react";
import Seo from "@/components/seo/Seo";
import { useLocalePath } from "@/i18n/LanguageSync";

export default function About() {
  const { t } = useTranslation("pages");
  const lp = useLocalePath();
  return (
    <>
      <Seo
        title={t("about.seoTitle")}
        description={t("about.seoDescription")}
        path="/about"
      />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 af-soft-glow pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-20 pb-20 md:pt-28">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <span
                data-testid="about-eyebrow"
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-600"
              >
                {t("about.eyebrow")}
              </span>
              <h1
                data-testid="about-title"
                className="font-display mt-6 text-[44px] sm:text-[56px] lg:text-[64px] leading-[1.02] text-[#0A1128]"
              >
                {t("about.heroTitle")}
              </h1>
              <p className="mt-6 text-[17px] leading-relaxed text-slate-600 max-w-2xl">
                {t("about.heroBody")}
              </p>
            </div>
            <div className="lg:col-span-5 rounded-3xl border border-slate-200 p-8 bg-[#F7F9FC]">
              <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">{t("about.missionEyebrow")}</p>
              <p className="font-display mt-3 text-[24px] leading-snug text-[#0A1128]">
                {t("about.missionBody")}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border-t border-slate-200/70">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-28">
          <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">{t("about.valuesEyebrow")}</p>
          <h2 className="font-display mt-3 text-[34px] md:text-[42px] leading-[1.05] text-[#0A1128] max-w-3xl">
            {t("about.valuesTitle")}
          </h2>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Compass, title: t("about.values.clarityTitle"), body: t("about.values.clarityBody") },
              { icon: ShieldCheck, title: t("about.values.securityTitle"), body: t("about.values.securityBody") },
              { icon: Sparkles, title: t("about.values.aiTitle"), body: t("about.values.aiBody") },
              { icon: Network, title: t("about.values.openTitle"), body: t("about.values.openBody") },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-2xl border border-slate-200 p-6 af-shadow-card">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#0A1128] text-white">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="font-display mt-5 text-[18px]">{title}</h3>
                <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F7F9FC] border-y border-slate-200/70">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-28">
          <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">{t("about.structureEyebrow")}</p>
          <h2 className="font-display mt-3 text-[34px] md:text-[42px] leading-[1.05] text-[#0A1128] max-w-3xl">
            {t("about.structureTitle")}
          </h2>

          <div className="mt-12 grid md:grid-cols-3 gap-5">
            <Link to={lp("/safe-to-spend")} data-testid="about-card-safe-to-spend" className="group rounded-2xl bg-white border border-slate-200 p-7 hover:-translate-y-1 transition-transform af-shadow-card">
              <span className="inline-flex items-center gap-2 text-[12px] font-semibold text-[#00A373]">
                <span className="h-2 w-2 rounded-full bg-[#00C48C]" /> {t("about.structure.stsBadge")}
              </span>
              <h3 className="font-display mt-4 text-[22px]">Safe to Spend 365</h3>
              <p className="mt-3 text-[14px] text-slate-600 leading-relaxed">
                {t("about.structure.stsBody")}
              </p>
            </Link>
            <Link to={lp("/ember")} data-testid="about-card-ember" className="group rounded-2xl bg-[#0A1128] text-white p-7 hover:-translate-y-1 transition-transform af-shadow-card">
              <span className="inline-flex items-center gap-2 text-[12px] font-semibold text-[#FF8E73]">
                <span className="h-2 w-2 rounded-full bg-[#FF5A36]" /> {t("about.structure.emberBadge")}
              </span>
              <h3 className="font-display mt-4 text-[22px]">Ember360</h3>
              <p className="mt-3 text-[14px] text-slate-300 leading-relaxed">
                {t("about.structure.emberBody")}
              </p>
            </Link>
            <Link to={lp("/platform")} data-testid="about-card-apis" className="group rounded-2xl bg-white border border-slate-200 p-7 hover:-translate-y-1 transition-transform af-shadow-card">
              <span className="inline-flex items-center gap-2 text-[12px] font-semibold text-[#2563EB]">
                <span className="h-2 w-2 rounded-full bg-[#2563EB]" /> {t("about.structure.apisBadge")}
              </span>
              <h3 className="font-display mt-4 text-[22px]">{t("about.structure.apisTitle")}</h3>
              <p className="mt-3 text-[14px] text-slate-600 leading-relaxed">
                {t("about.structure.apisBody")}
              </p>
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-[#0A1128] text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-[32px] md:text-[40px] leading-[1.05]">{t("about.ctaTitle")}</h2>
            <p className="mt-3 text-slate-300 max-w-md">
              {t("about.ctaBody")}
            </p>
          </div>
          <div className="flex flex-wrap md:justify-end gap-3">
            <Link to={lp("/contact")} data-testid="about-cta-contact" className="inline-flex items-center gap-2 rounded-full bg-white text-[#0A1128] text-[14px] font-medium px-5 py-3 hover:bg-slate-100">
              {t("about.ctaContact")}
            </Link>
            <Link to={lp("/platform")} data-testid="about-cta-apis" className="inline-flex items-center gap-2 rounded-full border border-white/20 text-[14px] font-medium px-5 py-3 hover:bg-white/5">
              {t("about.ctaApis")}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
